import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import EditPatient from "./EditPatient";
import AddAnotherRecordingButton from "./AddAnotherRecordingButton";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Button, BodyText, Flex, Stack } from "@knowtex-ai/components";
import SearchBar from "./SearchBar";
import sendServerLogging from "../../utils/sendServerLogging";
import userJSON from "../../utils/userJSON";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import PrechartingModal from "../dashboard/Precharting/PrechartingModal";
import { useTutorial } from "../Tutorial/useTutorial";
import { isStandaloneUser as getIsStandaloneUser } from "../../utils/standaloneUser";
import { resolveSpecialtyToDbValue, getSpecialtyLabelFromDbValue } from "../../utils/specialties";
import {
  getCrossSiteCareStatus,
  isCancelledPatient,
  isScheduledElsewherePatient,
} from "../../utils/patientCareStatus";
import { patientMatchesFilters } from "../../utils/noteListFilters";
import useDashboardUserInfo from "../../hooks/useDashboardUserInfo";
import {
  useProviderSettingsQuery,
  useSequencedProviderSettingsMutation,
} from "../../hooks/useProviderSettings";
import { getSessionToken } from "../../lib/sessionToken";

//Table of patients/notes.
/** Tutorial full flow: only this patient's card is targeted for ready_to_record and review_note steps */
const TUTORIAL_MOCK_PATIENT_DESCRIPTION = "QQQVAINTERFACE,IFC";

const toComparableSpecialty = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return "";
  }

  const dbValue = resolveSpecialtyToDbValue(trimmedValue);
  return (dbValue || trimmedValue).toLowerCase();
};

const toDisplaySpecialty = (value, fallback = "Not set") => {
  if (typeof value !== "string") {
    return fallback;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return fallback;
  }

  const dbValue = resolveSpecialtyToDbValue(trimmedValue);
  return getSpecialtyLabelFromDbValue(dbValue || trimmedValue);
};

// `legacyKey` matches the aliases the Smart Chart workspace reads, so the
// launcher agrees with older saved chart_history records.
const PRECHART_BASE_FIELDS = [
  { label: "ALLERGIES", key: "allergies" },
  { label: "VITALS", key: "vitals" },
  {
    label: "ACTIVE PROBLEMS",
    key: "past_medical_history",
    legacyKey: "problems",
  },
  { label: "MEDICATIONS", key: "medications" },
  { label: "LABS", key: "lab_results", legacyKey: "labs" },
];
const PRECHART_MAX_FIELDS = PRECHART_BASE_FIELDS.length;

const hasMeaningfulPrechartValue = (value) =>
  value !== null && value !== undefined && String(value).trim().length > 0;

const hasPrechartFieldValue = (parsedChartHistory, field) =>
  [field.key, field.legacyKey]
    .filter(Boolean)
    .some((key) => hasMeaningfulPrechartValue(parsedChartHistory?.[key]));

const PRECHART_CUSTOM_TEMPLATE_FIELD = {
  label: "ADDITIONAL CONTEXT",
  key: "custom_template",
};

const hasCustomTemplateValue = (parsedChartHistory = {}) => {
  const rawFlag = parsedChartHistory?.is_custom_template;
  if (rawFlag === true || rawFlag === false) {
    return rawFlag;
  }

  if (typeof rawFlag === "string") {
    const normalizedFlag = rawFlag.trim().toLowerCase();
    if (normalizedFlag === "true") {
      return true;
    }
    if (normalizedFlag === "false") {
      return false;
    }
  }

  return false;
};

const hasAppointmentIdValue = (appointmentId) =>
  appointmentId !== null && appointmentId !== undefined && String(appointmentId).trim().length > 0;

const getSmartChartFieldsForPatient = (
  parsedChartHistory = {},
  { hasAppointmentId = true } = {}
) => {
  if (!hasAppointmentId) {
    return [PRECHART_CUSTOM_TEMPLATE_FIELD];
  }

  return hasCustomTemplateValue(parsedChartHistory)
    ? [...PRECHART_BASE_FIELDS, PRECHART_CUSTOM_TEMPLATE_FIELD]
    : PRECHART_BASE_FIELDS;
};

const parsePatientChartHistory = (rawChartHistory) => {
  if (!rawChartHistory) return {};
  if (typeof rawChartHistory === "string") {
    try {
      return JSON.parse(rawChartHistory);
    } catch {
      return {};
    }
  }
  if (typeof rawChartHistory === "object") return rawChartHistory;
  return {};
};

const getPrechartFlagValue = (rawChartHistory) => {
  const parsedChartHistory = parsePatientChartHistory(rawChartHistory);
  const rawFlag = parsedChartHistory?.is_prechart;

  if (rawFlag === true || rawFlag === false) {
    return rawFlag;
  }

  if (typeof rawFlag === "string") {
    const normalizedFlag = rawFlag.trim().toLowerCase();
    if (normalizedFlag === "true") {
      return true;
    }
    if (normalizedFlag === "false") {
      return false;
    }
  }

  return null;
};

const getPrechartSelectionCount = (rawChartHistory, { hasAppointmentId = true } = {}) => {
  const parsedChartHistory = parsePatientChartHistory(rawChartHistory);
  const hasCustomTemplate = hasCustomTemplateValue(parsedChartHistory);
  if (!hasAppointmentId) {
    return hasCustomTemplate ? 1 : 0;
  }
  if (hasCustomTemplate) {
    return 1;
  }

  const prechartFlagValue = getPrechartFlagValue(parsedChartHistory);
  if (prechartFlagValue !== true) {
    return 0;
  }

  return PRECHART_BASE_FIELDS.reduce(
    (count, field) => (hasPrechartFieldValue(parsedChartHistory, field) ? count + 1 : count),
    0
  );
};

const getPrechartFieldCapacity = (rawChartHistory, { hasAppointmentId = true } = {}) => {
  const parsedChartHistory = parsePatientChartHistory(rawChartHistory);
  if (!hasAppointmentId) {
    return 1;
  }
  return hasCustomTemplateValue(parsedChartHistory) ? 1 : PRECHART_MAX_FIELDS;
};

const getPrechartSelectedFieldNames = (rawChartHistory, { hasAppointmentId = true } = {}) => {
  const parsedChartHistory = parsePatientChartHistory(rawChartHistory);
  const hasCustomTemplate = hasCustomTemplateValue(parsedChartHistory);
  if (!hasAppointmentId) {
    return hasCustomTemplate ? [PRECHART_CUSTOM_TEMPLATE_FIELD.label] : [];
  }
  if (hasCustomTemplate) {
    return [PRECHART_CUSTOM_TEMPLATE_FIELD.label];
  }

  const prechartFlagValue = getPrechartFlagValue(parsedChartHistory);
  // Only show selected-field labels when Smart Chart has been explicitly set.
  if (prechartFlagValue !== true) {
    return [];
  }

  return PRECHART_BASE_FIELDS.filter((field) =>
    hasPrechartFieldValue(parsedChartHistory, field)
  ).map((field) => field.label);
};

const getHasCustomPromptHintForPatient = (patient = {}) => {
  const rawFlag = patient?.has_custom_prompt ?? patient?.hasCustomPrompt;
  if (rawFlag === true || rawFlag === false) {
    return rawFlag;
  }

  if (typeof rawFlag === "string") {
    const normalizedFlag = rawFlag.trim().toLowerCase();
    if (normalizedFlag === "true") {
      return true;
    }
    if (normalizedFlag === "false") {
      return false;
    }
  }

  const inlinePrompt = patient?.custom_prompt;
  if (typeof inlinePrompt === "string") {
    return inlinePrompt.trim().length > 0;
  }

  return null;
};

const ListNotes = ({
  isLoading,
  allPatients,
  setPatientsChange,
  patientsChange,
  name,
  turnaroundTime,
  org,
  providerSpecialty = null,
  superOrg,
  getName,
  setIsEditing,
  isEditing,
  pickedDate,
  onRefreshSchedule,
  isRefreshing = false,
  submittingPatientId = null,
  setSubmittingPatientId,
  showAddPatientModal,
  setShowAddPatientModal,
  noteTitlesEntries = [],
  noteTitlesFavorites = [],
  setNoteTitlesFavorites,
  noteTitleOptionValues = [],
  noteTitleOptionValueSet,
  firstNoteTitleValue = "",
  isNoteTitlesEnabled = false,
}) => {
  const isStandaloneUser = getIsStandaloneUser();
  const safeAllPatients = useMemo(
    () => (Array.isArray(allPatients) ? allPatients : []),
    [allPatients]
  );
  const { isActive: isTutorialActive, isFullTutorial } = useTutorial();
  const pathName = window.location.pathname;
  const isDashboardTutorial = isTutorialActive && pathName === "/dashboard";
  const navigate = useNavigate();
  const isScheduleRefreshDateDisabled = useMemo(() => {
    const selectedDate = new Date(pickedDate);
    if (Number.isNaN(selectedDate.getTime())) {
      return false;
    }

    selectedDate.setHours(0, 0, 0, 0);
    const latestAllowedDate = new Date();
    latestAllowedDate.setHours(0, 0, 0, 0);
    latestAllowedDate.setDate(latestAllowedDate.getDate() + 15);

    return selectedDate > latestAllowedDate;
  }, [pickedDate]);
  const scheduleRefreshTooltip = isScheduleRefreshDateDisabled
    ? "You can only pull your patient schedule up to 15 days in advance in Knowtex."
    : "Pull your schedule for the selected date.";
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const [filterAnnouncement, setFilterAnnouncement] = useState("");
  // The count is read through a ref so schedule polling doesn't re-announce it.
  const visiblePatientCountRef = useRef(0);
  const hasFilterChangedRef = useRef(false);
  const filterButtonRef = useRef(null);
  const dashboardUserInfoQuery = useDashboardUserInfo();
  const currentUserId = dashboardUserInfoQuery.data?.user_id || safeAllPatients[0]?.user_id;
  const providerSettingsQuery = useProviderSettingsQuery(currentUserId);
  const updateProviderSettingsMutation = useSequencedProviderSettingsMutation(currentUserId);
  const lastLoadedUserIdRef = useRef(null);
  const filterOptionsRef = useRef(filterOptions);
  const filterRetryTimeoutRef = useRef(null);
  const filterPersistVersionRef = useRef(0);

  useEffect(() => {
    filterOptionsRef.current = filterOptions;
  }, [filterOptions]);

  useEffect(() => {
    return () => {
      if (filterRetryTimeoutRef.current) {
        clearTimeout(filterRetryTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (providerSettingsQuery.data?.settings && lastLoadedUserIdRef.current !== currentUserId) {
      if (filterRetryTimeoutRef.current) {
        clearTimeout(filterRetryTimeoutRef.current);
        filterRetryTimeoutRef.current = null;
      }
      filterPersistVersionRef.current += 1;
      lastLoadedUserIdRef.current = currentUserId;
      const initialFilters = providerSettingsQuery.data.settings.note_filters?.filter_options;
      // The tutorial runs against an unfiltered list, so saved filters must not
      // land back on top of the reset below when settings resolve mid-tour.
      if (Array.isArray(initialFilters) && !isDashboardTutorial) {
        setFilterOptions(initialFilters);
      } else {
        setFilterOptions([]);
      }
    }
  }, [providerSettingsQuery.data, currentUserId, isDashboardTutorial]);

  // Starting the tutorial on the dashboard puts the list back to "All" and drops
  // the search, so a narrowed list can't hide the patients the tour walks
  // through. The reset is local: saved filters stay in provider settings and
  // come back when the tour ends. Hydration has already run for this user by
  // then, so it won't reapply them on its own.
  const wasDashboardTutorialRef = useRef(false);
  useEffect(() => {
    if (isDashboardTutorial && !wasDashboardTutorialRef.current) {
      setFilterOptions([]);
      setSearchQuery("");
    } else if (!isDashboardTutorial && wasDashboardTutorialRef.current) {
      const savedFilters = providerSettingsQuery.data?.settings?.note_filters?.filter_options;
      if (Array.isArray(savedFilters)) {
        setFilterOptions(savedFilters);
      }
    }
    wasDashboardTutorialRef.current = isDashboardTutorial;
  }, [isDashboardTutorial, providerSettingsQuery.data]);
  const [showPrechartingModal, setShowPrechartingModal] = useState(false);
  const [prechartPatient, setPrechartPatient] = useState(null);
  const [prechartTooltipState, setPrechartTooltipState] = useState(null);
  const [specialtyMismatchContext, setSpecialtyMismatchContext] = useState(null);
  const [customPromptPresenceByPatientId, setCustomPromptPresenceByPatientId] = useState({});
  const [savedSmartChartSummaryByPatientId, setSavedSmartChartSummaryByPatientId] = useState({});
  const customPromptLookupScopeRef = useRef("");
  const lookedUpCustomPromptPatientIdsRef = useRef(new Set());
  const smartChartLauncherToRefocusRef = useRef(null);
  const isSmartChartWorkspaceOpen = showPrechartingModal && Boolean(prechartPatient);

  // The workspace has no focus trap, so closing it drops keyboard focus on
  // <body>. Hand it back to the launcher that opened it once the list is back.
  useEffect(() => {
    if (isSmartChartWorkspaceOpen) {
      return;
    }

    const patientId = smartChartLauncherToRefocusRef.current;
    smartChartLauncherToRefocusRef.current = null;
    if (patientId === null || patientId === undefined) {
      return;
    }

    const activeElement = document.activeElement;
    if (activeElement && activeElement !== document.body) {
      return;
    }

    const launcher = Array.from(document.querySelectorAll("[data-smart-chart-launcher]")).find(
      (element) => element.dataset.smartChartLauncher === String(patientId)
    );
    launcher?.focus();
  }, [isSmartChartWorkspaceOpen]);

  useEffect(() => {
    if (!isSmartChartWorkspaceOpen) {
      return undefined;
    }

    document.body.classList.add("smart-chart-2b-open");
    return () => {
      document.body.classList.remove("smart-chart-2b-open");
    };
  }, [isSmartChartWorkspaceOpen]);

  useEffect(() => {
    if (!isSmartChartWorkspaceOpen) {
      return undefined;
    }

    setIsEditing(true);
    return () => {
      setIsEditing(false);
    };
  }, [isSmartChartWorkspaceOpen, setIsEditing]);

  const handleSavedSmartChartSummary = useCallback(({ patientId, selectedCount, capacity }) => {
    if (patientId === null || patientId === undefined) {
      return;
    }

    const normalizedPatientId = String(patientId);
    const normalizedCapacity = Math.max(1, Number(capacity) || PRECHART_MAX_FIELDS);
    const normalizedSelectedCount = Math.min(
      normalizedCapacity,
      Math.max(0, Number(selectedCount) || 0)
    );

    setSavedSmartChartSummaryByPatientId((previous) => {
      const current = previous[normalizedPatientId];
      if (
        current?.selectedCount === normalizedSelectedCount &&
        current?.capacity === normalizedCapacity
      ) {
        return previous;
      }

      return {
        ...previous,
        [normalizedPatientId]: {
          selectedCount: normalizedSelectedCount,
          capacity: normalizedCapacity,
        },
      };
    });
  }, []);

  // Saved summaries bridge the gap until refreshed dashboard rows become authoritative.
  useEffect(() => {
    setSavedSmartChartSummaryByPatientId((previous) =>
      Object.keys(previous).length > 0 ? {} : previous
    );
  }, [safeAllPatients]);

  const sendOpenNoteLog = (mrn, patientId, status) => {
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "OPEN_NOTE",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          Status: status,
        },
      },
    });
  };

  const userId = safeAllPatients[0]?.user_id;
  const isFullTutorialFlow = isTutorialActive && isFullTutorial;

  const sortedByDateOfAppt = (patients) => {
    const safePatients = Array.isArray(patients) ? patients : [];
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const groupedByDate = safePatients.reduce((acc, patient) => {
      const date = new Date(patient.date_of_appt);
      const month = months[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();

      const key = `${month} ${day}, ${year}`;

      const existingGroup = acc.find((item) => item.date === key);
      if (existingGroup) {
        existingGroup.patients.push(patient);
      } else {
        acc.push({
          date: key,
          patients: [patient],
          monthYear: `${month} ${year}`,
        });
      }
      return acc;
    }, []);
    return groupedByDate;
  };

  const sortedPatientsByMonthDay = useMemo(
    () => sortedByDateOfAppt(safeAllPatients),
    [safeAllPatients]
  );

  useEffect(() => {
    const scopeKey = `${userId || ""}|${pickedDate || ""}`;
    if (customPromptLookupScopeRef.current === scopeKey) {
      return;
    }

    customPromptLookupScopeRef.current = scopeKey;
    lookedUpCustomPromptPatientIdsRef.current = new Set();
    setCustomPromptPresenceByPatientId({});
  }, [userId, pickedDate]);

  useEffect(() => {
    let intervalId;

    if (!isEditing) {
      intervalId = setInterval(getName, 5000);
    }
    return () => clearInterval(intervalId);
  }, [patientsChange, isEditing]);

  const FILTER_PERSIST_MAX_RETRIES = 3;
  const FILTER_PERSIST_BASE_DELAY_MS = 1000;
  const FILTER_PERSIST_MAX_DELAY_MS = 30000;

  const persistFilterOptionsRef = useRef();

  const persistFilterOptions = useCallback(
    (nextFilters, attempt = 0, version = null) => {
      if (version === null) {
        if (filterRetryTimeoutRef.current) {
          clearTimeout(filterRetryTimeoutRef.current);
          filterRetryTimeoutRef.current = null;
        }
      }

      const currentVersion = version ?? ++filterPersistVersionRef.current;

      updateProviderSettingsMutation
        .mutateSequenced({
          note_filters: {
            filter_options: nextFilters,
          },
        })
        .catch((err) => {
          if (filterPersistVersionRef.current !== currentVersion) {
            return;
          }

          if (attempt < FILTER_PERSIST_MAX_RETRIES) {
            const delay = Math.min(
              FILTER_PERSIST_BASE_DELAY_MS * Math.pow(2, attempt),
              FILTER_PERSIST_MAX_DELAY_MS
            );
            filterRetryTimeoutRef.current = setTimeout(() => {
              filterRetryTimeoutRef.current = null;
              if (filterPersistVersionRef.current !== currentVersion) {
                return;
              }
              persistFilterOptionsRef.current?.(nextFilters, attempt + 1, currentVersion);
            }, delay);
          } else {
            console.error("Failed to persist filter options:", err);
            Sentry.captureException(err, {
              tags: { feature: "provider_settings", action: "save_filter_options" },
              extra: { filterOptions: nextFilters },
            });
          }
        });
    },
    [updateProviderSettingsMutation]
  );

  useEffect(() => {
    persistFilterOptionsRef.current = persistFilterOptions;
  }, [persistFilterOptions]);

  const handleFilterOptionsChange = useCallback(
    (nextFilters) => {
      filterOptionsRef.current = nextFilters;
      hasFilterChangedRef.current = true;
      setFilterOptions(nextFilters);
      persistFilterOptions(nextFilters);
    },
    [persistFilterOptions]
  );

  const toggleFilterOption = useCallback(
    (value) => {
      const current = filterOptionsRef.current;
      const nextFilters = current.includes(value)
        ? current.filter((f) => f !== value)
        : [...current, value];
      handleFilterOptionsChange(nextFilters);
    },
    [handleFilterOptionsChange]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isFilterOpen) {
      const firstCheckbox = filterRef.current?.querySelector('input[type="checkbox"]');
      firstCheckbox?.focus();
    }
  }, [isFilterOpen]);

  const handleToggleNA = async (patientId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/${patientId}/no-show`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to toggle N/A status");
      }

      const data = await response.json();

      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: data.isNA ? "PATIENT_MARKED_NA" : "PATIENT_MARKED_AVAILABLE",
          eventData: { patientId: patientId },
        },
      });

      setPatientsChange((prev) => !prev);
      toast.info(data.message);
    } catch (error) {
      console.error("Error toggling N/A status:", error);
      toast.error("Failed to update patient status. Please try again.");
    }
  };

  const handleToggleDeclined = async (patientId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/${patientId}/declined`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to toggle Declined status");
      }

      const data = await response.json();

      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: data.isDeclined ? "PATIENT_MARKED_DECLINED" : "PATIENT_MARKED_AVAILABLE",
          eventData: { patientId: patientId },
        },
      });

      setPatientsChange((prev) => !prev);
      toast.info(data.message);
    } catch (error) {
      console.error("Error toggling Declined status:", error);
      toast.error("Failed to update patient status. Please try again.");
    }
  };

  const normalizedSearchQuery = useMemo(() => (searchQuery || "").toLowerCase(), [searchQuery]);

  const filteredPatients = useMemo(
    () =>
      sortedPatientsByMonthDay.flatMap((appointment) => {
        const appointmentPatients = Array.isArray(appointment?.patients)
          ? appointment.patients
          : [];
        const matchedDateParts =
          appointment?.date?.match(/(\b\d+\b)|(\b\d{2}\d{2}\d{4}\b)|(\b[A-Z][a-z]+\b)/g) || [];

        const isDateMatched =
          matchedDateParts.some((a) => a?.toLowerCase().includes(normalizedSearchQuery)) ||
          appointment?.date?.toLowerCase().includes(normalizedSearchQuery) ||
          appointment?.date
            ?.toLowerCase()
            .split(",")
            .flatMap((word) => word.split(","))
            .join("")
            .includes(normalizedSearchQuery);

        const isMonthYearMatched = appointment?.monthYear
          ?.toLowerCase()
          .includes(normalizedSearchQuery);

        const matchedPatientsDescrMrn = appointmentPatients.filter((patient) => {
          return (
            (patient?.mrn?.includes(normalizedSearchQuery) ||
              patient?.description?.toLowerCase().includes(normalizedSearchQuery) ||
              patient?.description
                ?.toLowerCase()
                .match(/(\w+)/g)
                ?.some((word) => word.includes(normalizedSearchQuery))) &&
            patientMatchesFilters(patient, filterOptions)
          );
        });

        const isStatusMatched = appointmentPatients.filter((patient) =>
          patientMatchesFilters(patient, filterOptions)
        );

        if ((isMonthYearMatched || isDateMatched) && isStatusMatched.length > 0) {
          return [{ ...appointment, patients: isStatusMatched }];
        }
        if (matchedPatientsDescrMrn.length > 0) {
          return [{ ...appointment, patients: matchedPatientsDescrMrn }];
        }
        return [];
      }),
    [sortedPatientsByMonthDay, normalizedSearchQuery, filterOptions]
  );

  const getStatusClassName = (status, patientType) => {
    switch (status) {
      case "Ready to Record":
        return "bg-ktx-green";
      case "Review":
        if (patientType === "cancelled") {
          return "bg-dark-purple-80";
        } else {
          return "bg-accent2";
        }
      case "Processing Audio":
        return "bg-gray-500";
      case "Locked":
        return "bg-dark-purple";
      case "Uploading Audio":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  const getHasCustomPromptForPatient = useCallback(
    (patient = {}) => {
      const hintedValue = getHasCustomPromptHintForPatient(patient);
      if (hintedValue !== null) {
        return hintedValue;
      }

      const patientId = patient?.patient_id;
      if (!patientId) {
        return false;
      }

      return customPromptPresenceByPatientId[patientId] === true;
    },
    [customPromptPresenceByPatientId]
  );

  const buildRecordState = useCallback(
    (patient) => ({
      patientID: patient.patient_id,
      patientName: patient.description,
      doctorName: name,
      MRN: patient.mrn,
      turnaroundTime,
      org,
      identifier: patient.demographics,
      userId,
      user_id: userId,
      chartHistory: patient.chart_history,
      hasCustomPromptHint: getHasCustomPromptForPatient(patient),
    }),
    [name, org, turnaroundTime, userId, getHasCustomPromptForPatient]
  );

  const closeSpecialtyMismatchModal = useCallback(() => {
    setSpecialtyMismatchContext(null);
  }, []);

  const handleRecordNavigation = useCallback(
    (patient) => {
      const recordState = buildRecordState(patient);
      const patientComparableSpecialty = toComparableSpecialty(patient?.patient_specialty);
      const providerComparableSpecialty = toComparableSpecialty(providerSpecialty);

      if (
        !patientComparableSpecialty ||
        !providerComparableSpecialty ||
        patientComparableSpecialty === providerComparableSpecialty
      ) {
        navigate(`/record/${patient.patient_id}`, { state: recordState });
        return;
      }

      setSpecialtyMismatchContext({
        patientId: patient.patient_id,
        recordState,
        patientName: patient.description,
        patientSpecialtyLabel: toDisplaySpecialty(patient.patient_specialty),
        providerSpecialtyLabel: toDisplaySpecialty(providerSpecialty),
      });
    },
    [buildRecordState, navigate, providerSpecialty]
  );

  const handleConfirmSpecialtyMismatch = useCallback(() => {
    if (!specialtyMismatchContext) {
      return;
    }

    navigate(`/record/${specialtyMismatchContext.patientId}`, {
      state: specialtyMismatchContext.recordState,
    });
    setSpecialtyMismatchContext(null);
  }, [navigate, specialtyMismatchContext]);

  const generateRecordLink = (patient, { confirmLeave } = {}) => (
    <AddAnotherRecordingButton
      locked={patient.status === "Locked"}
      confirmLeave={confirmLeave}
      onRecord={() => handleRecordNavigation(patient)}
    />
  );

  const canAccessPrecharting = (patient) => patient.status === "Ready to Record";

  const handleOpenPrecharting = (patient) => {
    setPrechartTooltipState(null);
    smartChartLauncherToRefocusRef.current = patient.patient_id;
    setPrechartPatient(patient);
    setShowPrechartingModal(true);
  };

  const PRECHART_TOOLTIP_ID = "prechart-details-tooltip";
  const isFocusVisible = (element) => {
    try {
      return element.matches(":focus-visible");
    } catch {
      return true;
    }
  };

  const handleShowPrechartTooltip = (
    event,
    patientId,
    selectedFieldNames = [],
    prechartFlagValue = null,
    hasCustomPrompt = false
  ) => {
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const TOOLTIP_OFFSET = 8;
    const VIEWPORT_MARGIN = 12;
    const BASE_TOOLTIP_HEIGHT = 28;
    const ESTIMATED_LINE_HEIGHT = 18;
    const selectedLines = selectedFieldNames.length > 0 ? selectedFieldNames.length : 1;
    const customPromptLines = hasCustomPrompt ? 2 : 0; // divider + label
    const estimatedTooltipHeight =
      BASE_TOOLTIP_HEIGHT + (selectedLines + customPromptLines) * ESTIMATED_LINE_HEIGHT;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : null;
    const preferredTop = buttonRect.bottom + TOOLTIP_OFFSET;
    const canRenderBelow =
      viewportHeight == null ||
      preferredTop + estimatedTooltipHeight <= viewportHeight - VIEWPORT_MARGIN;
    const computedTop = canRenderBelow
      ? preferredTop
      : Math.max(VIEWPORT_MARGIN, buttonRect.top - estimatedTooltipHeight - TOOLTIP_OFFSET);

    setPrechartTooltipState({
      patientId,
      selectedFieldNames,
      prechartFlagValue,
      hasCustomPrompt,
      left: buttonRect.left + buttonRect.width / 2,
      top: computedTop,
    });
  };

  const handleHidePrechartTooltip = () => {
    setPrechartTooltipState(null);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const totalNotes = useMemo(
    () =>
      filteredPatients.reduce(
        (acc, entry) => acc + (Array.isArray(entry?.patients) ? entry.patients.length : 0),
        0
      ),
    [filteredPatients]
  );
  const totalPages = useMemo(() => Math.ceil(totalNotes / 25), [totalNotes]);

  const getPatientsForPage = () => {
    const startIndex = (currentPage - 1) * 25;
    const endIndex = startIndex + 25;

    let accumulatedPatients = [];
    let accumulatedCount = 0;

    for (let i = 0; i < filteredPatients.length; i++) {
      const { date, patients } = filteredPatients[i];
      const safePatientsForDate = Array.isArray(patients) ? patients : [];
      const patientsCount = safePatientsForDate.length;

      for (let j = 0; j < patientsCount; j++) {
        if (accumulatedCount >= startIndex && accumulatedCount < endIndex) {
          if (!accumulatedPatients.some((item) => item.date === date)) {
            accumulatedPatients.push({ date, patients: [] });
          }
          accumulatedPatients
            .find((item) => item.date === date)
            .patients.push(safePatientsForDate[j]);
        }
        accumulatedCount++;

        if (accumulatedCount >= endIndex) {
          break;
        }
      }
      if (accumulatedCount >= endIndex) {
        break;
      }
    }
    return accumulatedPatients;
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const notesToShow = useMemo(
    () => (pathName === "/notes" ? getPatientsForPage() : filteredPatients),
    [pathName, currentPage, filteredPatients]
  );

  const isRenderablePatient = (patient) =>
    Boolean(patient && patient.patient_id != null && patient.description);

  const hasAnyRenderablePatients = useMemo(
    () => safeAllPatients.some(isRenderablePatient),
    [safeAllPatients]
  );

  const renderableNotesToShow = useMemo(
    () =>
      notesToShow
        .map((group) => ({
          ...group,
          patients: (Array.isArray(group?.patients) ? group.patients : []).filter(
            isRenderablePatient
          ),
        }))
        .filter((group) => group.patients.length > 0),
    [notesToShow]
  );

  const notesForDisplay = useMemo(() => {
    if (!isSmartChartWorkspaceOpen) {
      return renderableNotesToShow;
    }

    return [
      {
        date: "workspace",
        patients: [prechartPatient],
      },
    ];
  }, [isSmartChartWorkspaceOpen, prechartPatient, renderableNotesToShow]);

  const visiblePatients = useMemo(
    () =>
      renderableNotesToShow.flatMap((group) =>
        Array.isArray(group?.patients) ? group.patients : []
      ),
    [renderableNotesToShow]
  );
  visiblePatientCountRef.current = visiblePatients.length;

  useEffect(() => {
    if (!hasFilterChangedRef.current) {
      return;
    }
    const count = visiblePatientCountRef.current;
    const scope = filterOptions.length === 0 ? "all statuses" : filterOptions.join(", ");
    setFilterAnnouncement(`${count} ${count === 1 ? "patient" : "patients"} shown for ${scope}`);
  }, [filterOptions]);

  const lastAnnouncedSearchRef = useRef(searchQuery);
  useEffect(() => {
    if (lastAnnouncedSearchRef.current === searchQuery) {
      return undefined;
    }
    // Wait for typing to pause so JAWS reads one count, not one per keystroke.
    const timer = setTimeout(() => {
      lastAnnouncedSearchRef.current = searchQuery;
      const count = visiblePatientCountRef.current;
      setFilterAnnouncement(
        searchQuery.trim()
          ? `${count} ${count === 1 ? "patient matches" : "patients match"} your search`
          : `Search cleared. ${count} ${count === 1 ? "patient" : "patients"} shown`
      );
    }, 600);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const hintedPresence = safeAllPatients.reduce((acc, patient) => {
      const patientId = patient?.patient_id;
      if (!patientId) {
        return acc;
      }

      const hintedValue = getHasCustomPromptHintForPatient(patient);
      if (hintedValue !== null) {
        lookedUpCustomPromptPatientIdsRef.current.add(String(patientId));
        acc[patientId] = hintedValue;
      }
      return acc;
    }, {});

    if (Object.keys(hintedPresence).length === 0) {
      return;
    }

    setCustomPromptPresenceByPatientId((previous) => ({
      ...hintedPresence,
      ...previous,
    }));
  }, [safeAllPatients]);

  useEffect(() => {
    if (pathName !== "/dashboard") {
      return;
    }

    const patientIdsToLookup = safeAllPatients.reduce((acc, patient) => {
      const patientId = patient?.patient_id;
      if (!patientId) {
        return acc;
      }

      const normalizedPatientId = String(patientId);
      const hintedValue = getHasCustomPromptHintForPatient(patient);
      if (hintedValue !== null) {
        return acc;
      }

      if (lookedUpCustomPromptPatientIdsRef.current.has(normalizedPatientId)) {
        return acc;
      }

      acc.push(normalizedPatientId);
      return acc;
    }, []);

    if (patientIdsToLookup.length === 0) {
      return;
    }

    let isCancelled = false;

    const loadCustomPromptPresence = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/notes/todos/smart-chart-presence`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              token: getSessionToken(),
            },
            body: JSON.stringify({ patientIds: patientIdsToLookup }),
          }
        );

        if (!response.ok) {
          return;
        }

        const payload = await response.json().catch(() => ({}));
        const presenceByPatientId =
          payload && typeof payload === "object" && payload.presenceByPatientId
            ? payload.presenceByPatientId
            : {};

        if (isCancelled) {
          return;
        }

        patientIdsToLookup.forEach((patientId) => {
          lookedUpCustomPromptPatientIdsRef.current.add(patientId);
        });

        setCustomPromptPresenceByPatientId((previous) => {
          const nextState = { ...previous };
          patientIdsToLookup.forEach((patientId) => {
            nextState[patientId] = presenceByPatientId?.[patientId] === true;
          });
          return nextState;
        });
      } catch (error) {
        console.error("Failed to load smart chart prompt presence:", error);
      }
    };

    loadCustomPromptPresence();

    return () => {
      isCancelled = true;
    };
  }, [pathName, safeAllPatients]);

  const hasPatientData = hasAnyRenderablePatients;
  const hasScheduleResponseArray = Array.isArray(allPatients);
  const hasExplicitNoPatientsResponse =
    !isLoading && hasScheduleResponseArray && !hasAnyRenderablePatients;
  const showGeneralizedScheduleLoading =
    pathName === "/dashboard" && pickedDate != null && (isLoading || !hasScheduleResponseArray);
  const firstReviewPatientId = visiblePatients.find(
    (patient) =>
      patient.status === "Review" &&
      patient.patient_type !== "na" &&
      patient.patient_type !== "declined" &&
      !isCancelledPatient(patient)
  )?.patient_id;
  const firstVisiblePatientId = visiblePatients[0]?.patient_id;
  const firstReadyToRecordPatientId = visiblePatients.find(
    (patient) =>
      patient.status === "Ready to Record" &&
      patient.patient_type !== "na" &&
      patient.patient_type !== "declined" &&
      !isCancelledPatient(patient)
  )?.patient_id;
  // Full-tutorial anchor targets: prefer the mock tutorial patient (any
  // "QQQVAINTERFACE,IFC*" variant), otherwise fall back to the first eligible
  // real patient so the tour still has elements to highlight and click.
  // visiblePatients is in render order (top -> bottom); the test patient is
  // appended to the bottom, so target the LAST match to always anchor on the
  // most recently added test patient even if older duplicates sit above it.
  const testPatientMatches = visiblePatients.filter(
    (patient) =>
      typeof patient?.description === "string" &&
      patient.description.startsWith(TUTORIAL_MOCK_PATIENT_DESCRIPTION)
  );
  const mockTutorialPatientId =
    testPatientMatches.length > 0
      ? testPatientMatches[testPatientMatches.length - 1].patient_id
      : undefined;
  const tutorialReviewPatientId = mockTutorialPatientId ?? firstReviewPatientId;
  const tutorialReadyToRecordPatientId = mockTutorialPatientId ?? firstReadyToRecordPatientId;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [totalPages]);

  useEffect(() => {
    if (!prechartTooltipState) {
      return undefined;
    }

    const dismissTooltip = () => setPrechartTooltipState(null);
    window.addEventListener("scroll", dismissTooltip, true);
    window.addEventListener("resize", dismissTooltip);

    return () => {
      window.removeEventListener("scroll", dismissTooltip, true);
      window.removeEventListener("resize", dismissTooltip);
    };
  }, [prechartTooltipState]);

  const formatTime = (time) => {
    if (!time) return "";

    return time
      .split(":")
      .slice(0, 2)
      .map((part) => part.padStart(2, "0"))
      .join(":");
  };

  const prechartingModalElement =
    showPrechartingModal && prechartPatient ? (
      <PrechartingModal
        setShowPrechartingModal={setShowPrechartingModal}
        mrn={prechartPatient?.demographics}
        patientName={prechartPatient.description}
        patientID={prechartPatient.patient_id}
        diarizedTranscript={prechartPatient.diarized_transcript}
        noteCitations={prechartPatient.cited_note}
        specialty={providerSpecialty}
        crossSiteCareStatus={getCrossSiteCareStatus(prechartPatient)}
        appointmentId={prechartPatient?.appointment_id}
        onCustomPromptSaved={({ patientId, hasCustomPrompt }) => {
          if (!patientId) {
            return;
          }

          lookedUpCustomPromptPatientIdsRef.current.add(String(patientId));
          setCustomPromptPresenceByPatientId((previous) => ({
            ...previous,
            [patientId]: hasCustomPrompt === true,
          }));
        }}
        onSubmitSuccess={({ chartHistory, selectionSummary } = {}) => {
          if (selectionSummary) {
            handleSavedSmartChartSummary(selectionSummary);
          }
          if (chartHistory && typeof chartHistory === "object") {
            setPrechartPatient((previous) =>
              previous
                ? {
                    ...previous,
                    chart_history: chartHistory,
                  }
                : previous
            );
          }
          setPatientsChange(true);
          // Don't close here: closing immediately would unmount the modal
          // before the submit button's "Saved!" success state can render.
          // The user dismisses via the Skip/Close button instead.
        }}
      />
    ) : null;

  return (
    <div
      id="dashboard-list-notes"
      className={`flex flex-col ${isSmartChartWorkspaceOpen ? "min-h-0 flex-1 h-full" : ""}`}
      data-smart-chart-workspace={isSmartChartWorkspaceOpen ? "true" : undefined}
    >
      {!isSmartChartWorkspaceOpen && (
        <>
          <div className="flex items-baseline w-full laptop:w-list justify-between">
            <div className="flex items-baseline">
              <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
              {totalPages > 1 && pathName === "/notes" && (
                <div className="text-accent1 flex mt-2 ml-3">
                  <div className="flex h-[18px] min-w-[32px]">
                    {totalPages > 1 && (
                      <button
                        type="button"
                        aria-label="Previous page"
                        className={`mr-2 ${currentPage === 1 ? "opacity-40 cursor-not-allowed" : ""}`}
                        aria-disabled={currentPage === 1}
                        onClick={() => currentPage !== 1 && goToPage(currentPage - 1)}
                      >
                        <svg
                          className="text-transparent-grey hover:text-light-grey w-6 h-6 text-gray-800 dark:text-white"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="m15 19-7-7 7-7"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  <span className="">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="flex min-w-[32px]">
                    {totalPages > 1 && (
                      <button
                        type="button"
                        aria-label="Next page"
                        className={`ml-2 ${currentPage === totalPages ? "opacity-40 cursor-not-allowed" : ""}`}
                        aria-disabled={currentPage === totalPages}
                        onClick={() => currentPage !== totalPages && goToPage(currentPage + 1)}
                      >
                        <svg
                          className="text-transparent-grey hover:text-light-grey w-6 h-6 text-gray-800 dark:text-white"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="m9 5 7 7-7 7"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div
              className="flex relative items-baseline"
              ref={filterRef}
              onBlur={(e) => {
                if (isFilterOpen && !filterRef.current?.contains(e.relatedTarget)) {
                  setIsFilterOpen(false);
                }
              }}
            >
              <label
                id="filter-status-label"
                htmlFor="filter-status-trigger"
                className="mr-2 font-semibold"
              >
                Filter by Status
              </label>
              <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                {filterAnnouncement}
              </p>
              <div className="relative">
                <button
                  ref={filterButtonRef}
                  type="button"
                  id="filter-status-trigger"
                  onClick={() => setIsFilterOpen((prev) => !prev)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setIsFilterOpen(false);
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1 border rounded-md text-knowtex-grey bg-white hover:bg-gray-50 transition-colors kx-focus"
                  aria-expanded={isFilterOpen}
                  aria-controls="filter-status-dropdown"
                  aria-labelledby="filter-status-label filter-status-value"
                >
                  <span id="filter-status-value" className="text-sm">
                    {filterOptions.length === 0
                      ? "All"
                      : filterOptions.length === 1
                        ? filterOptions[0]
                        : `${filterOptions.length} selected`}
                  </span>
                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className={`h-3.5 w-3.5 shrink-0 transition-transform duration-150 ${
                      isFilterOpen ? "rotate-180" : ""
                    }`}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                    />
                  </svg>
                </button>

                {isFilterOpen && (
                  <fieldset
                    id="filter-status-dropdown"
                    className="absolute right-0 mt-1 w-52 bg-white border border-gray-200 rounded-md shadow-lg z-20 py-1"
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setIsFilterOpen(false);
                        filterButtonRef.current?.focus();
                      }
                    }}
                  >
                    <legend className="sr-only">Filter by Status</legend>
                    <label
                      htmlFor="filter-opt-All"
                      className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        id="filter-opt-All"
                        checked={filterOptions.length === 0}
                        onChange={() => handleFilterOptionsChange([])}
                        className="h-4 w-4 rounded border-gray-300 accent-accent2 cursor-pointer kx-focus"
                      />
                      <span className="text-knowtex-grey">All</span>
                    </label>

                    <div role="separator" className="my-1 border-t border-gray-100" />

                    {[
                      "Ready to Record",
                      "Review",
                      "Processing Audio",
                      "Locked",
                      "N/A",
                      "Declined",
                      "Scheduled Elsewhere",
                      "Cancelled",
                    ].map((option) => {
                      const id = `filter-opt-${option
                        .replace(/\s+/g, "-")
                        .replace(/[^a-zA-Z0-9-]/g, "")}`;
                      return (
                        <label
                          key={option}
                          htmlFor={id}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <input
                            type="checkbox"
                            id={id}
                            checked={filterOptions.includes(option)}
                            onChange={() => toggleFilterOption(option)}
                            className="h-4 w-4 rounded border-gray-300 accent-accent2 cursor-pointer kx-focus"
                          />
                          <span className="text-knowtex-grey">{option}</span>
                        </label>
                      );
                    })}
                  </fieldset>
                )}
              </div>
            </div>
          </div>
          {pickedDate != null && pathName === "/dashboard" && (
            <div className="flex items-center gap-3 mt-4 mb-2">
              <button
                type="button"
                id="add_veteran"
                onClick={() => setShowAddPatientModal(true)}
                className="hover:cursor-pointer py-2 pl-4 pr-8 rounded-md text-background1 bg-accent1/85 hover:bg-accent1/95 flex items-center gap-2 w-auto transition-colors duration-200 kx-focus"
              >
                <svg
                  aria-hidden="true"
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Veteran
              </button>
            </div>
          )}
          {pathName === "/dashboard" ? (
            <div className="flex mt-2 w-full items-center gap-2">
              <h2 className="text-lg text-accent1">
                Total Patients for the Day: {hasPatientData ? safeAllPatients.length : 0}
              </h2>
              {onRefreshSchedule && !isStandaloneUser && (
                <span className="inline-flex" title={scheduleRefreshTooltip}>
                  <button
                    id="schedule_refresh"
                    type="button"
                    onClick={onRefreshSchedule}
                    disabled={isRefreshing || isScheduleRefreshDateDisabled}
                    aria-label={scheduleRefreshTooltip}
                    className={`px-3 py-2 rounded font-bold transition-colors flex items-center ${
                      isRefreshing || isScheduleRefreshDateDisabled
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-blue-600 hover:text-blue-800 cursor-pointer"
                    }`}
                  >
                    <svg
                      className={`w-4 h-4 mr-2 transition-transform duration-300 ${
                        isRefreshing ? "animate-spin" : "hover:rotate-180"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    {isRefreshing ? "Refreshing..." : "Refresh Schedule"}
                  </button>
                </span>
              )}
            </div>
          ) : null}
        </>
      )}
      {!isSmartChartWorkspaceOpen && filteredPatients.length === 0 && hasPatientData ? (
        <div className="flex items-center w-full laptop:w-list justify-center mt-4">
          <h2 className="text-lg text-light-grey">No Results</h2>
        </div>
      ) : null}
      {showGeneralizedScheduleLoading ? (
        <div className="flex items-center w-full laptop:w-list justify-center mt-4">
          <div className="flex items-center text-light-grey">
            <svg
              className="animate-spin h-5 w-5 mr-2"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4Z"
              />
            </svg>
            <h2 className="text-lg">Loading schedule for the selected date...</h2>
          </div>
        </div>
      ) : hasExplicitNoPatientsResponse && pathName === "/dashboard" ? (
        <div className="flex items-center w-full laptop:w-list justify-center mt-4">
          <h2 className="text-lg text-light-grey">
            There are no patients registered for this date.
          </h2>
        </div>
      ) : null}
      {!showGeneralizedScheduleLoading &&
        notesForDisplay.map((sortedPatients) => (
          <div
            className={`container m-0 ${
              isSmartChartWorkspaceOpen ? "flex min-h-0 flex-1 flex-col" : ""
            }`}
            key={sortedPatients.date}
          >
            {pathName === "/notes" && !isSmartChartWorkspaceOpen ? (
              <h2
                key={sortedPatients.date}
                className="text-accent1 mt-4 border-b-2 text-xl border-[#E5E7EB]"
              >
                {sortedPatients.date}
              </h2>
            ) : null}
            {sortedPatients.patients &&
              sortedPatients.patients.length !== 0 &&
              sortedPatients.patients[0].patient_id !== null && (
              // `contents` keeps the existing flex layout; the explicit roles keep
              // list semantics, which some browsers drop with display: contents.
              <ul role="list" aria-label={
                sortedPatients.date === "workspace"
                  ? "Selected patient"
                  : `Patients, ${sortedPatients.date}`
              } className="contents">
              {sortedPatients.patients.map((patient) => {
                const isCancelled = isCancelledPatient(patient);
                const crossSiteCareStatus = getCrossSiteCareStatus(patient);
                const isBlockingCancellation = isCancelled;

                return (
                  <li role="listitem" className="contents" key={patient.patient_id}>
                    <div
                      data-testid={`patient-card-${patient.patient_id}`}
                      className={`note flex flex-wrap laptop:flex-nowrap w-full laptop:w-list items-center gap-3 ${
                        isSmartChartWorkspaceOpen
                          ? "smart-chart-2b-patient-card shrink-0"
                          : "mb-2 mt-2"
                      }`}
                    >
                      {/* Patient Card */}
                      <div
                        id={
                          // review_note anchors on the actual Review button (see
                          // EditPatient), not the whole card. Only ready_to_record
                          // stays on the card.
                          isFullTutorialFlow
                            ? patient.status === "Ready to Record" &&
                              patient.patient_id === tutorialReadyToRecordPatientId
                              ? "ready_to_record"
                              : undefined
                            : patient.status === "Ready to Record" &&
                                patient.patient_id === firstReadyToRecordPatientId
                              ? "ready_to_record"
                              : undefined
                        }
                        className={`flex items-center flex-1 justify-between text-background1 border rounded-2xl shadow-drop-shadow ${getStatusClassName(
                          patient.status,
                          crossSiteCareStatus && patient.status === "Review"
                            ? null
                            : patient.patient_type
                        )} p-3 ${
                          patient.patient_type === "na"
                            ? "opacity-50 border-2 border-yellow-500"
                            : patient.patient_type === "declined"
                              ? "opacity-50 border-2 border-red-500"
                              : crossSiteCareStatus
                                ? "border-2 border-sky-300"
                                : isCancelled
                                  ? "border-2 border-gray-500"
                                  : ""
                        }`}
                      >
                        <div className="flex items-center">
                          {patient.patient_type === "na" && (
                            <span className="mr-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded font-bold">
                              N/A
                            </span>
                          )}
                          {patient.patient_type === "declined" && (
                            <span className="mr-2 bg-red-500 text-white text-xs px-2 py-1 rounded font-bold">
                              DECLINED
                            </span>
                          )}
                          {crossSiteCareStatus && (
                            <span
                              className="mr-2 inline-flex items-center bg-sky-700 text-white text-xs px-2 py-1 rounded font-bold"
                              title={`Expected care location: ${crossSiteCareStatus.facilityName} — ${crossSiteCareStatus.displayName}${crossSiteCareStatus.sta3n ? ` (Station ${crossSiteCareStatus.sta3n})` : ""}. Relaunch from this Point of Care before using Refetch Data, Refresh Vitals, or Save & Upload to EHR.`}
                            >
                              SCHEDULED ELSEWHERE
                              <span className="sr-only">
                                {`. Expected care location: ${crossSiteCareStatus.facilityName}, ${crossSiteCareStatus.displayName}${crossSiteCareStatus.sta3n ? `, station ${crossSiteCareStatus.sta3n}` : ""}. Relaunch from this Point of Care before using Refetch Data, Refresh Vitals, or Save and Upload to EHR.`}
                              </span>
                              <span
                                aria-hidden="true"
                                className="ml-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-white text-[10px] font-bold leading-none"
                              >
                                i
                              </span>
                            </span>
                          )}
                          {isCancelled && (
                            <span className="mr-2 bg-gray-500 text-white text-xs px-2 py-1 rounded font-bold">
                              CANCELLED
                            </span>
                          )}
                          <div className="flex-col items-center">
                            <h3 className="text-xl mobile:text-lg ">{patient.description}</h3>
                            <p className="text-md font-thin mobile:text-sm">
                              {patient.demographics}
                            </p>
                            <div className="flex items-center">
                              <p className="text-md font-thin mobile:text-sm">
                                {patient.surgery === true ? "Surgery" : "Visit"}
                              </p>
                              <p className="text-md font-thin mobile:text-sm ml-2">
                                {formatTime(patient.appt_time)}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          {/* Keep EditPatient mounted while uploading — swapping it for a
                              spinner unmounts the open review modal (and its Bootstrap
                              instance) as soon as submitToVAAndLock sets submittingPatientId. */}
                          {submittingPatientId === patient.patient_id && (
                            <div className="flex items-center">
                              <svg
                                className="animate-spin h-5 w-5 mr-2 text-background1"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              <span className="text-background1 text-lg">Uploading to EHR...</span>
                            </div>
                          )}
                            <>
                              {patient.status === "Review" ? (
                                patient.patient_type === "na" ||
                                patient.patient_type === "declined" ? (
                                  <div className="text-background1 text-xl opacity-50 cursor-not-allowed">
                                    Review
                                  </div>
                                ) : (
                                  <EditPatient
                                    patient={patient}
                                    isCrossSiteCare={Boolean(crossSiteCareStatus)}
                                    isTutorialReviewAnchor={
                                      isTutorialActive &&
                                      patient.patient_id === tutorialReviewPatientId
                                    }
                                    setPatientsChange={setPatientsChange}
                                    org={org}
                                    superOrg={superOrg}
                                    providerSpecialty={providerSpecialty}
                                    setIsEditing={setIsEditing}
                                    isEditing={isEditing}
                                    name={name}
                                    getName={getName}
                                    generateRecordLink={generateRecordLink}
                                    status={patient.status}
                                    ehrUploadStatus={patient.ehr_upload_status}
                                    submittingPatientId={submittingPatientId}
                                    setSubmittingPatientId={setSubmittingPatientId}
                                    sendOpenNoteLog={sendOpenNoteLog}
                                    noteTitlesEntries={noteTitlesEntries}
                                    noteTitlesFavorites={noteTitlesFavorites}
                                    setNoteTitlesFavorites={setNoteTitlesFavorites}
                                    noteTitleOptionValues={noteTitleOptionValues}
                                    noteTitleOptionValueSet={noteTitleOptionValueSet}
                                    firstNoteTitleValue={firstNoteTitleValue}
                                    isNoteTitlesEnabled={isNoteTitlesEnabled}
                                  />
                                )
                              ) : patient.status === "Ready to Record" ? (
                                <div className="flex items-center">
                                  {canAccessPrecharting(patient) &&
                                    (() => {
                                      const hasAppointmentId = hasAppointmentIdValue(
                                        patient?.appointment_id
                                      );
                                      const parsedChartHistory = parsePatientChartHistory(
                                        patient.chart_history
                                      );
                                      const hasCustomTemplateSelected =
                                        hasCustomTemplateValue(parsedChartHistory);
                                      const prechartFlagValue =
                                        getPrechartFlagValue(parsedChartHistory);
                                      const persistedSelectionCount = getPrechartSelectionCount(
                                        patient.chart_history,
                                        { hasAppointmentId }
                                      );
                                      const persistedFieldCapacity = getPrechartFieldCapacity(
                                        patient.chart_history,
                                        { hasAppointmentId }
                                      );
                                      const savedSelectionSummary =
                                        savedSmartChartSummaryByPatientId[
                                          String(patient.patient_id)
                                        ];
                                      const prechartSelectionCount =
                                        savedSelectionSummary?.selectedCount ??
                                        persistedSelectionCount;
                                      const prechartFieldCapacity =
                                        savedSelectionSummary?.capacity ?? persistedFieldCapacity;
                                      const hasPrechartSelections = prechartSelectionCount > 0;
                                      const hasExplicitPrechartState =
                                        hasCustomTemplateSelected ||
                                        prechartFlagValue === true ||
                                        (!hasAppointmentId && hasPrechartSelections);
                                      const shouldShowPrechartSelectionCount =
                                        Boolean(savedSelectionSummary) || hasExplicitPrechartState;
                                      const tooltipPrechartFlagValue = hasExplicitPrechartState
                                        ? prechartFlagValue
                                        : null;
                                      const hasSavedCustomPrompt =
                                        getHasCustomPromptForPatient(patient) === true;
                                      const selectedFieldNames = getPrechartSelectedFieldNames(
                                        patient.chart_history,
                                        { hasAppointmentId }
                                      );

                                      const showTooltip = (event) =>
                                        handleShowPrechartTooltip(
                                          event,
                                          patient.patient_id,
                                          selectedFieldNames,
                                          tooltipPrechartFlagValue,
                                          hasSavedCustomPrompt
                                        );

                                      return (
                                        <div className="relative mr-4">
                                          <button
                                            type="button"
                                            id={
                                              isFullTutorialFlow
                                                ? patient.patient_id ===
                                                  tutorialReadyToRecordPatientId
                                                  ? "prechart_button"
                                                  : undefined
                                                : patient.patient_id === firstReadyToRecordPatientId
                                                  ? "prechart_button"
                                                  : undefined
                                            }
                                            data-smart-chart-launcher={patient.patient_id}
                                            onClick={() => handleOpenPrecharting(patient)}
                                            onMouseEnter={showTooltip}
                                            onFocus={(event) => {
                                              // Mouse users already get it on hover; skip focus from clicks and modal close.
                                              if (isFocusVisible(event.currentTarget)) showTooltip(event);
                                            }}
                                            onMouseLeave={() => handleHidePrechartTooltip()}
                                            onBlur={() => handleHidePrechartTooltip()}
                                            onKeyDown={(event) => {
                                              if (event.key === "Escape") handleHidePrechartTooltip();
                                            }}
                                            aria-describedby={
                                              prechartTooltipState?.patientId === patient.patient_id
                                                ? PRECHART_TOOLTIP_ID
                                                : undefined
                                            }
                                            className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-accent1/75 text-background1 hover:bg-accent1/100 transition-colors text-sm font-semibold"
                                          >
                                            {hasPrechartSelections && (
                                              <span
                                                aria-hidden="true"
                                                className="text-white text-xs"
                                              >
                                                ✓
                                              </span>
                                            )}
                                            <span>Smart Chart</span>
                                            <span className="sr-only">{` for ${patient.description}`}</span>
                                            {shouldShowPrechartSelectionCount && (
                                              <span className="text-white text-xs font-semibold">
                                                {prechartSelectionCount}/{prechartFieldCapacity}
                                              </span>
                                            )}
                                            {hasSavedCustomPrompt ? (
                                              <span
                                                aria-hidden="true"
                                                className="text-xs font-black text-emerald-200"
                                              >
                                                ✚
                                              </span>
                                            ) : null}
                                            {hasSavedCustomPrompt ? (
                                              <span className="sr-only">, custom prompt</span>
                                            ) : null}
                                          </button>
                                        </div>
                                      );
                                    })()}
                                  <span
                                    id={
                                      (
                                        isFullTutorialFlow
                                          ? patient.patient_id === tutorialReadyToRecordPatientId
                                          : patient.patient_id === firstReadyToRecordPatientId
                                      )
                                        ? "ready_to_record_button"
                                        : undefined
                                    }
                                    className="flex items-center"
                                  >
                                    <div className="pr-1">
                                      <svg
                                        className="w-6 h-6 text-background1"
                                        aria-hidden="true"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          stroke="currentColor"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M19 9v3a5 5 0 0 1-5 5h-4a5 5 0 0 1-5-5V9m7 9v3m-3 0h6M11 3h2a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3Z"
                                        />
                                      </svg>
                                    </div>
                                    {patient.patient_type === "na" ||
                                    patient.patient_type === "declined" ||
                                    isBlockingCancellation ? (
                                      <span className="text-background1 text-xl mobile:text-lg opacity-50 cursor-not-allowed">
                                        Ready to Record
                                      </span>
                                    ) : (
                                      <div className="flex items-center gap-3">
                                        <Link
                                          data-testid="record-link"
                                          to={`/record/${patient.patient_id}`}
                                          state={buildRecordState(patient)}
                                          className="!text-background1 text-xl mobile:text-lg hover:opacity-80"
                                          onClick={(event) => {
                                            event.preventDefault();
                                            sendServerLogging({
                                              body: {
                                                ...userJSON(localStorage.getItem("userName")),
                                                event: "READY_TO_RECORD_CLICK",
                                                eventData: { mrn: patient.mrn },
                                                patientId: patient.patient_id,
                                              },
                                            });
                                            handleRecordNavigation(patient);
                                          }}
                                        >
                                          Ready to Record
                                          <span className="sr-only">{` for ${patient.description}`}</span>
                                        </Link>
                                      </div>
                                    )}
                                  </span>
                                </div>
                              ) : patient.status === "Processing Audio" ? (
                                patient.patient_type === "na" ||
                                patient.patient_type === "declined" ||
                                isBlockingCancellation ? (
                                  <div className="text-background1 text-xl opacity-50 cursor-not-allowed">
                                    Processing Audio
                                  </div>
                                ) : (
                                  <Link
                                    to={`/record/${patient.patient_id}`}
                                    state={buildRecordState(patient)}
                                    className="!text-background1 text-xl hover:opacity-80"
                                  >
                                    {patient.status}
                                    <span className="sr-only">{` for ${patient.description}`}</span>
                                  </Link>
                                )
                              ) : patient.status === "Locked" ? (
                                patient.patient_type === "na" ||
                                patient.patient_type === "declined" ? (
                                  <div className="text-background1 text-xl opacity-50 cursor-not-allowed">
                                    Locked
                                  </div>
                                ) : (
                                  <EditPatient
                                    patient={patient}
                                    isCrossSiteCare={Boolean(crossSiteCareStatus)}
                                    isTutorialReviewAnchor={
                                      isTutorialActive &&
                                      patient.patient_id === tutorialReviewPatientId
                                    }
                                    setPatientsChange={setPatientsChange}
                                    org={org}
                                    superOrg={superOrg}
                                    providerSpecialty={providerSpecialty}
                                    setIsEditing={setIsEditing}
                                    isEditing={isEditing}
                                    name={name}
                                    getName={getName}
                                    generateRecordLink={generateRecordLink}
                                    status={patient.status}
                                    ehrUploadStatus={patient.ehr_upload_status}
                                    submittingPatientId={submittingPatientId}
                                    setSubmittingPatientId={setSubmittingPatientId}
                                    identifier={patient.demographics}
                                    noteTitlesEntries={noteTitlesEntries}
                                    noteTitlesFavorites={noteTitlesFavorites}
                                    setNoteTitlesFavorites={setNoteTitlesFavorites}
                                    noteTitleOptionValues={noteTitleOptionValues}
                                    noteTitleOptionValueSet={noteTitleOptionValueSet}
                                    firstNoteTitleValue={firstNoteTitleValue}
                                    isNoteTitlesEnabled={isNoteTitlesEnabled}
                                  />
                                )
                              ) : (
                                <div
                                  className={`text-background1 text-xl ${getStatusClassName(
                                    patient.status,
                                    patient.patient_type
                                  )}`}
                                >
                                  {patient.status}
                                </div>
                              )}
                            </>
                        </div>
                      </div>

                      {/* Keep appointment disposition controls out of the expanded 2b workspace. */}
                      {pathName === "/dashboard" && !isSmartChartWorkspaceOpen && (
                        <div
                          id={
                            patient.patient_id === firstVisiblePatientId
                              ? "na_declined_toggles"
                              : undefined
                          }
                          className="flex items-end gap-6"
                        >
                          <div className="flex flex-col items-center">
                            <label
                              htmlFor={`na-toggle-${patient.patient_id}`}
                              className="text-xs font-semibold text-accent mb-1"
                            >
                              N/A
                            </label>
                            <label
                              className={`relative inline-flex items-center ${
                                isCancelled || patient.patient_type === "declined"
                                  ? "cursor-not-allowed opacity-40"
                                  : "cursor-pointer"
                              } focus-within:ring-2 focus-within:ring-accent1 focus-within:rounded-full`}
                            >
                              <input
                                id={`na-toggle-${patient.patient_id}`}
                                type="checkbox"
                                aria-label={`N/A, ${patient.description}`}
                                checked={patient.patient_type === "na"}
                                onChange={() => handleToggleNA(patient.patient_id)}
                                disabled={isCancelled || patient.patient_type === "declined"}
                                className="sr-only peer"
                                title={
                                  isCancelled
                                    ? "Cancelled appointments cannot be changed"
                                    : patient.patient_type === "declined"
                                      ? "Patient already marked as Declined"
                                      : patient.patient_type === "na"
                                        ? "Mark as Available"
                                        : "Mark as N/A"
                                }
                              />
                              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-ktx-purple peer-disabled:opacity-40 peer-disabled:cursor-not-allowed"></div>
                            </label>
                          </div>

                          <div className="flex flex-col items-center">
                            <label
                              htmlFor={`declined-toggle-${patient.patient_id}`}
                              className="text-xs font-semibold text-accent mb-1"
                            >
                              Declined
                            </label>
                            <label
                              className={`relative inline-flex items-center ${
                                isCancelled || patient.patient_type === "na"
                                  ? "cursor-not-allowed opacity-40"
                                  : "cursor-pointer"
                              } focus-within:ring-2 focus-within:ring-accent1 focus-within:rounded-full`}
                            >
                              <input
                                id={`declined-toggle-${patient.patient_id}`}
                                type="checkbox"
                                aria-label={`Declined, ${patient.description}`}
                                checked={patient.patient_type === "declined"}
                                onChange={() => handleToggleDeclined(patient.patient_id)}
                                disabled={isCancelled || patient.patient_type === "na"}
                                className="sr-only peer"
                                title={
                                  isCancelled
                                    ? "Cancelled appointments cannot be changed"
                                    : patient.patient_type === "na"
                                      ? "Patient already marked as N/A"
                                      : patient.patient_type === "declined"
                                        ? "Mark as Available"
                                        : "Mark as Declined"
                                }
                              />
                              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-ktx-purple peer-disabled:opacity-40 peer-disabled:cursor-not-allowed"></div>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                    {isSmartChartWorkspaceOpen ? prechartingModalElement : null}
                  </li>
                );
              })}
              </ul>
            )}
          </div>
        ))}
      {totalPages > 1 && pathName === "/notes" && !isSmartChartWorkspaceOpen && (
        <div className="text-accent1 flex justify-end mt-2 ml-3">
          <div className="flex h-[18px] min-w-[32px]">
            {totalPages > 1 && (
              <button
                type="button"
                aria-label="Previous page"
                className={`mr-2 ${currentPage === 1 ? "opacity-40 cursor-not-allowed" : ""}`}
                aria-disabled={currentPage === 1}
                onClick={() => currentPage !== 1 && goToPage(currentPage - 1)}
              >
                <svg
                  className="text-transparent-grey hover:text-light-grey w-6 h-6 text-gray-800 dark:text-white"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m15 19-7-7 7-7"
                  />
                </svg>
              </button>
            )}
          </div>
          <span className="">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex min-w-[32px]">
            {totalPages > 1 && (
              <button
                type="button"
                aria-label="Next page"
                className={`ml-2 ${currentPage === totalPages ? "opacity-40 cursor-not-allowed" : ""}`}
                aria-disabled={currentPage === totalPages}
                onClick={() => currentPage !== totalPages && goToPage(currentPage + 1)}
              >
                <svg
                  className="text-transparent-grey hover:text-light-grey w-6 h-6 text-gray-800 dark:text-white"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m9 5 7 7-7 7"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
      {prechartTooltipState &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            id={PRECHART_TOOLTIP_ID}
            role="tooltip"
            className="pointer-events-none fixed rounded-md bg-gray-900 px-3 py-2 text-xs font-semibold text-white shadow-lg"
            style={{
              left: prechartTooltipState.left,
              top: prechartTooltipState.top,
              transform: "translateX(-50%)",
              zIndex: 2147483647,
              maxHeight: "calc(100vh - 24px)",
              overflowY: "auto",
            }}
          >
            <div className="flex flex-col whitespace-nowrap">
              <div
                className={
                  prechartTooltipState.hasCustomPrompt
                    ? "flex flex-col gap-1 border-b border-gray-700 pb-1.5 mb-1.5"
                    : "flex flex-col gap-1"
                }
              >
                {prechartTooltipState.selectedFieldNames.length > 0 ? (
                  prechartTooltipState.selectedFieldNames.map((fieldName) => (
                    <span key={fieldName}>{fieldName}</span>
                  ))
                ) : prechartTooltipState.prechartFlagValue === false ? (
                  <span>No Smart Chart objects selected.</span>
                ) : (
                  <span>Click to start Smart Chart for this patient.</span>
                )}
              </div>
              {prechartTooltipState.hasCustomPrompt ? (
                <span className="inline-flex items-center gap-1 font-black leading-none text-emerald-300">
                  <span aria-hidden="true">✚</span>
                  <span>Custom Prompt</span>
                </span>
              ) : null}
            </div>
          </div>,
          document.body
        )}
      <Modal
        open={Boolean(specialtyMismatchContext)}
        onOpenChange={(open) => {
          if (!open) {
            closeSpecialtyMismatchModal();
          }
        }}
        title="Specialty mismatch"
        subtitle="This patient's last recording specialty differs from your active specialty."
      >
        <Stack gap={16}>
          <BodyText>
            {`${
              specialtyMismatchContext?.patientName || "This patient"
            } is tagged as ${specialtyMismatchContext?.patientSpecialtyLabel || "Unknown specialty"}, while your current provider specialty is ${
              specialtyMismatchContext?.providerSpecialtyLabel || "Unknown specialty"
            }.`}
          </BodyText>
          <BodyText>Continue if you want to proceed with this recording anyway.</BodyText>
          <Flex justifyContent="flex-end" gap={12}>
            <Button variant="secondary" onClick={closeSpecialtyMismatchModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmSpecialtyMismatch}>
              Continue to Record
            </Button>
          </Flex>
        </Stack>
      </Modal>
    </div>
  );
};

export default ListNotes;
